import { BaseError, NetworkError } from '@sdk/errors';
import { AxiosError } from 'axios';

export type ErrorResponse<T = any> = {
  data: T | null;
  error: BaseError | null;
};

interface ServerErrorOptions {
  permanent?: boolean;
  successMessage?: string;
  errorMessage?: string;
  onReload?: () => void;
}
// Helper to get error message from any error type
export const getErrorMessage = (error: unknown) => {
  if (error instanceof AxiosError) {
    return error.response?.data?.message || error.message;
  }
  return error instanceof Error ? error.message : 'An unexpected error occurred';
};

// Server-side error handler - now returns a simpler structure
export async function handleServerError<T>(
  action: () => Promise<T>,
  fallbackData?: T,
): Promise<{ data: T | undefined; error: string | null }> {
  try {
    const data = await action();
    return { data, error: null };
  } catch (error) {
    return {
      data: fallbackData,
      error: getErrorMessage(error),
    };
  }
}

// Service error handler - to be used within services
export async function handleServiceError<T>(
  action: () => Promise<T>,
  fallbackData?: T,
): Promise<ErrorResponse<T>> {
  try {
    const data = await action();
    return { data, error: null };
  } catch (error: any) {

    return {
      data: fallbackData ?? null,
      error: new NetworkError(getErrorMessage(error)),
    };
  }
}

// Client-side error handler - for UI components
export function handleClientError(error: string | null, options: Partial<ServerErrorOptions> = {}) {
  if (error) {
    // Default options for client errors - not permanent by default
    const defaultOptions = {
      permanent: false,
    };

    // Merge with provided options
    const finalOptions = { ...defaultOptions, ...options };

    // Show toast notification
    console.error({
      variant: 'destructive',
      title: 'Error',
      description: finalOptions.errorMessage || error,
      permanent: finalOptions.permanent,
      onOpenChange: finalOptions.onReload
        ? (open: boolean) => {
            if (!open && finalOptions.onReload) {
              finalOptions.onReload();
            }
          }
        : undefined,
    });
  } else if (options.successMessage) {

  }
}
